import { useState } from 'react';
import { deactivateMealCreditApi } from 'src/components/oneCorp/features/MealCredit/hooks/deactivateMealCreditApi';
import { useAsyncTaskHandler } from 'src/common/utils/useAsyncTaskHandler';

export interface UseDeactivateMealCredit {
  deactivateMealCredit: () => Promise<void>;
  status: DeactivateMealCreditStatus;
  isDeactivating: boolean;
  errors: string[];
}

export interface UseDeactivateMealCreditProps {
  corpId: string;
  mealCreditId: string;
  onSuccess: () => Promise<void>;
  onError: () => Promise<void>;
}

export enum DeactivateMealCreditStatus {
  IDLE,
  DEACTIVATING,
}

export const useDeactivateMealCredit = ({
  corpId,
  mealCreditId,
  onError,
  onSuccess,
}: UseDeactivateMealCreditProps): UseDeactivateMealCredit => {
  const { startAsyncTask, errors } = useAsyncTaskHandler({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const [status, setStatus] = useState<DeactivateMealCreditStatus>(
    DeactivateMealCreditStatus.IDLE
  );

  const isDeactivating = status === DeactivateMealCreditStatus.DEACTIVATING;

  async function deactivateMealCredit(): Promise<void> {
    setStatus(DeactivateMealCreditStatus.DEACTIVATING);

    await startAsyncTask(() => {
      return deactivateMealCreditApi({ corpId, mealCreditId });
    });
  }

  async function handleSuccess(): Promise<void> {
    await onSuccess();
  }

  async function handleError(): Promise<void> {
    await onError();
    setStatus(DeactivateMealCreditStatus.IDLE);
  }

  return {
    deactivateMealCredit,
    status,
    isDeactivating,
    errors,
  };
};
